// The Sentry/GlitchTip DSN — one parse, used by both ends.
//
// It lives in `domain` for the same reason `REDACTION_PATTERNS` does: the two
// readers sit in packages that cannot see each other. `@developerz/config`'s
// `ENV_REGISTRY` needs the shape to REFUSE a malformed `SENTRY_DSN` at boot, and
// `@developerz/observability` needs the parsed parts to build an envelope URL —
// and observability imports config, so config can never import observability. A
// second copy of "what a DSN looks like" in the registry is exactly the drift
// that lets boot accept a value the reporter then cannot use.
//
// **No message here ever echoes the value.** A DSN carries the project's write
// key in its userinfo, and `validateAppEnv` prints a schema issue's `message`
// verbatim into a boot log. Every refusal names the RULE.

import { z } from 'zod';

/** A parsed DSN — everything the transport needs, and nothing it doesn't. */
export type SentryDsn = {
  /** The public (write) key. Goes in `X-Sentry-Auth`, never in a log line. */
  readonly publicKey: string;
  /** The numeric project id the ingest path is keyed by. */
  readonly projectId: string;
  /** Where one envelope is POSTed. */
  readonly envelopeUrl: string;
  /**
   * `host/projectId` — the boot log's identity for this sink. Deliberately
   * key-free: an operator needs to know WHICH project is armed, and a boot line
   * that carried the key would put a write credential in every pod's logs.
   */
  readonly describe: string;
};

/** Why a DSN was refused. Names the rule; never carries the value. */
export class SentryDsnError extends Error {
  constructor(rule: string) {
    super(`SENTRY_DSN ${rule}`);
    this.name = 'SentryDsnError';
  }
}

/**
 * Parse `{scheme}://{publicKey}@{host}{path}/{projectId}` into the parts the
 * transport needs. Throws {@link SentryDsnError} on anything else — a DSN that
 * is present and wrong is a configured sink that reports nothing, which is the
 * failure this whole seam exists to end, so it is refused rather than dropped.
 *
 * A `user:password@` DSN is refused outright. Sentry retired the secret half of
 * the DSN in 2016 and GlitchTip never had one, so a password here is either a
 * decade-old key or a pasted admin credential — and the value ends up in an
 * `X-Sentry-Auth` header we build by hand. Refusing at the boundary is the only
 * place that can tell.
 */
export function parseSentryDsn(raw: string): SentryDsn {
  if (!URL.canParse(raw)) throw new SentryDsnError('must be a valid URL');
  const url = new URL(raw);
  if (!['http:', 'https:'].includes(url.protocol)) {
    throw new SentryDsnError('must be an HTTP(S) URL');
  }
  if (url.username === '') {
    throw new SentryDsnError('must carry the project public key as its userinfo (key@host/…)');
  }
  if (url.password !== '') {
    throw new SentryDsnError(
      'must not carry a password (key:secret@) — the DSN secret half was retired in 2016 and this value is sent as an auth header',
    );
  }
  // `/12345` → base `''`; `/prefix/12345` → base `/prefix`. A trailing slash is
  // tolerated because a copy-paste from the Sentry UI sometimes has one.
  const segments = url.pathname.split('/').filter((s) => s !== '');
  const projectId = segments.at(-1);
  if (projectId === undefined || !/^\d+$/.test(projectId)) {
    throw new SentryDsnError('must end in the numeric project id (…/<project_id>)');
  }
  const base = segments.slice(0, -1).join('/');
  const prefix = base === '' ? '' : `/${base}`;
  return {
    publicKey: url.username,
    projectId,
    envelopeUrl: `${url.origin}${prefix}/api/${projectId}/envelope/`,
    describe: `${url.host}/${projectId}`,
  };
}

/**
 * The registry entry's schema — {@link parseSentryDsn}'s rules, not a second
 * statement of them. A value this admits is one the reporter can build a
 * transport from, by construction.
 */
export const SentryDsnSchema: z.ZodType<string, string> = z
  .string()
  .min(1)
  .superRefine((value, ctx) => {
    try {
      parseSentryDsn(value);
    } catch (err) {
      ctx.addIssue({
        code: 'custom',
        // `SentryDsnError` already prefixes the var name; the registry prefixes
        // it again, so strip ours and leave the rule.
        message:
          err instanceof SentryDsnError
            ? err.message.replace(/^SENTRY_DSN /, '')
            : 'could not be parsed as a DSN',
      });
    }
  });
